import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getMeasure,
  getMeasuresPatient,
  getMeasuresPatientsHeatMap,
  getPatientTsvMeasureTypes,
  retryMeasureIntegration,
} from 'services/api';
import { normalizeDataMeasure, normalizeDataMeasures, normalizeHeatMap } from './operators';
import { pushNotification } from 'features/notifications/notificationsSlice';
import { i18n } from 'utils/i18n';

export interface MeasuresState {
  measuresList: {
    data: any;
    status: null | 'idle' | 'loading' | 'failed';
  };
  heatMap: {
    data: [];
    pagination: {
      total: number;
      page: number;
    };
    status: null | 'idle' | 'loading' | 'failed';
  };
  measure: {
    data: null | any;
    status: null | 'idle' | 'loading' | 'failed';
  };
  tsvMeasureTypes: {
    patientId: null | string; // paciente al que pertenece la lista cargada
    types: string[]; // códigos de tipo que su grupo sincroniza vía TSV
    status: null | 'idle' | 'loading' | 'failed';
  };
}

const initialState: MeasuresState = {
  measuresList: {
    data: [],
    status: null,
  },
  heatMap: {
    data: [],
    pagination: {
      total: 0,
      page: 1,
    },
    status: null,
  },
  measure: {
    data: null,
    status: null,
  },
  tsvMeasureTypes: {
    patientId: null,
    types: [],
    status: null,
  },
};

export const getMeasuresPatientAsync = createAsyncThunk('measures/getMeasuresPatient', async (data) => {
  return await getMeasuresPatient(data);
});

export const getMeasuresPatientsHeatMapAsync = createAsyncThunk('measures/getMeasuresPatientsHeatMap', async (data) => {
  const response = await getMeasuresPatientsHeatMap(data);
  return response?.data;
});

export const getPatientTsvMeasureTypesAsync: any = createAsyncThunk(
  'measures/getPatientTsvMeasureTypes',
  async (patientId: string) => {
    const response = await getPatientTsvMeasureTypes(patientId);
    return { patientId, types: response?.data?.items ?? [] };
  },
  {
    // Dedup con estado FRESCO (getState en el momento del dispatch, no el snapshot de render de
    // cada componente): N montajes simultáneos producen UNA petición. 'failed' no bloquea, así
    // que un fallo transitorio se reintenta en el siguiente montaje.
    condition: (patientId: string, { getState }) => {
      if (!patientId) return false;
      const current = (getState() as any).measures.tsvMeasureTypes;
      const alreadyHandled =
        current.patientId === patientId && (current.status === 'loading' || current.status === 'idle');
      return !alreadyHandled;
    },
  },
);

export const retryMeasureIntegrationAsync: any = createAsyncThunk(
  'measures/retryIntegration',
  async (data: { measureId: string; patientId: string; measureRawId: string }, { getState, dispatch }) => {
    const response = await retryMeasureIntegration(data.measureId);
    const success = response?.data?.success;
    if (success) {
      dispatch(pushNotification({ message: i18n('measure.integration.retry.success'), severity: 'success' }));
      const state: any = getState();
      const measureStatus = state.masters?.measuresStatus?.data ?? [];
      const measuresGenericTypes = state.masters?.measuresGenericTypes?.data ?? [];
      dispatch(
        getMeasureAsync({
          patientId: data.patientId,
          measureRawId: data.measureRawId,
          measureStatus,
          measuresGenericTypes,
        }),
      );
    } else {
      dispatch(pushNotification({ message: i18n('measure.integration.retry.error'), severity: 'error' }));
    }
    return response;
  },
);

export const getMeasureAsync: any | null = createAsyncThunk('measures/getMeasure', async (data: any, { getState }) => {
  const response = await getMeasure(data);
  const state: any = getState();
  const measuresTypes: any[] = state.masters?.measuresTypes?.data ?? [];
  const measureTypeLookup: Record<string, string> = Object.fromEntries(
    measuresTypes.map((t: any) => [t.code, t.label ?? t.code]),
  );
  return { response, measureTypeLookup };
});

export const measuresSlice = createSlice({
  name: 'measures',
  initialState,
  reducers: {
    setSelectPage: (state, action: PayloadAction<number>) => {
      state.heatMap.pagination.page = action.payload;
    },
    setMeasure: (state, action: PayloadAction<any>) => {
      state.measure.data = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMeasuresPatientAsync.pending, (state) => {
        state.measuresList.status = 'loading';
      })
      .addCase(getMeasuresPatientAsync.fulfilled, (state, action) => {
        state.measuresList.status = 'idle';
        state.measuresList.data = normalizeDataMeasures(state.measuresList.data, action.payload, action.meta.arg);
      })
      .addCase(getMeasuresPatientAsync.rejected, (state) => {
        state.measuresList.status = 'failed';
      })
      .addCase(getMeasuresPatientsHeatMapAsync.pending, (state) => {
        state.heatMap.status = 'loading';
      })
      .addCase(getMeasuresPatientsHeatMapAsync.fulfilled, (state, action) => {
        state.heatMap.status = 'idle';
        state.heatMap.data = normalizeHeatMap(action.payload, action.meta.arg);
        state.heatMap.pagination.total = action.payload.item?.total;
      })
      .addCase(getMeasuresPatientsHeatMapAsync.rejected, (state) => {
        state.heatMap.status = 'failed';
      })
      .addCase(getPatientTsvMeasureTypesAsync.pending, (state, action) => {
        state.tsvMeasureTypes.status = 'loading';
        state.tsvMeasureTypes.patientId = action.meta.arg;
        // Sin lista heredada del paciente anterior: mejor icono ausente un instante que uno ajeno
        state.tsvMeasureTypes.types = [];
      })
      .addCase(getPatientTsvMeasureTypesAsync.fulfilled, (state, action) => {
        // Gana el último DISPATCH, no la última respuesta: una respuesta tardía de un paciente
        // anterior no pisa la lista del paciente en pantalla (pending ya fijó patientId).
        if (action.payload.patientId !== state.tsvMeasureTypes.patientId) return;
        state.tsvMeasureTypes.status = 'idle';
        state.tsvMeasureTypes.types = action.payload.types;
      })
      .addCase(getPatientTsvMeasureTypesAsync.rejected, (state, action) => {
        if (action.meta.arg !== state.tsvMeasureTypes.patientId) return;
        state.tsvMeasureTypes.status = 'failed';
        state.tsvMeasureTypes.types = [];
      })
      .addCase(getMeasureAsync.pending, (state) => {
        state.measure.status = 'loading';
      })
      .addCase(getMeasureAsync.fulfilled, (state, action) => {
        state.measure.data = normalizeDataMeasure(
          action.payload?.response,
          action.meta.arg,
          action.payload?.measureTypeLookup ?? {},
        );
        state.measure.status = 'idle';
      })
      .addCase(getMeasureAsync.rejected, (state) => {
        state.measure.status = 'failed';
      });
  },
});

export const { setSelectPage, setMeasure } = measuresSlice.actions;

export default measuresSlice.reducer;
